import { useState, useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { ErrorType, QUESTIONNAIRE_URL } from "env-vars";
import { clearQuestionnaire, openQuestionnaire } from "helpers";
import { createSurveySession } from "helpers/create-survey-session";
import { useTransifexLoader } from "context/transifex-loader";
import { useFeatureFlags } from "context/admin-content";
import { StartAssessment } from "components/start-assessment";
import { useEhrConnection } from "hooks/use-ehr-connection";

export function InitialSurvey() {
  const history = useHistory();
  const [isSubmitting, setSubmitting] = useState(false);
  const [pendingStart, setPendingStart] = useState(false);
  const [error, setError] = useState<ErrorType | null>(null);
  const { lang: locale } = useTransifexLoader();
  const { enableInternalQuestionnaire } = useFeatureFlags();
  const { isLoading: isLoadingEhr } = useEhrConnection();
  const hasStartedSurvey = useRef(false);

  // Auto-start survey if returning from EHR flow
  useEffect(() => {
    const ehrFlowComplete = sessionStorage.getItem("ehr_flow_complete");
    
    if (ehrFlowComplete && !hasStartedSurvey.current) {
      hasStartedSurvey.current = true;
      sessionStorage.removeItem("ehr_flow_complete");
      startSurvey();
    }
  }, []);

  // Handle pending start when EHR loading completes
  useEffect(() => {
    if (pendingStart && !isLoadingEhr) {
      setPendingStart(false);
      proceedAfterEhrCheck();
    }
  }, [pendingStart, isLoadingEhr]);

  const startSurvey = useCallback(async () => {
    setSubmitting(true);

    const patientId = localStorage.getItem("ciq_id");
    const idToken = localStorage.getItem("id_token");

    if (!patientId || !idToken) {
      setError(ErrorType.AUTH);

      return;
    }

    try {
      const response = await createSurveySession({
        isInitial: true,
        isRemote: true,
      });

      setSubmitting(false);

      if (!response.ok) {
        console.error("Error initializing survey session");

        setError(response.status === 401 ? ErrorType.AUTH : ErrorType.GENERAL);

        return;
      }

      const json = await response.json();
      localStorage.removeItem("surveyType");

      if (json.survey_type === "remote") {
        clearQuestionnaire();

        if (enableInternalQuestionnaire) {
          history.push(`/q/${json.survey_token}?locale=${locale || "en"}`);
        } else {
          localStorage.setItem("surveyCode", json.code);
          localStorage.setItem("sessionId", json.id);
          localStorage.setItem("surveyType", "remote");
          localStorage.setItem("ciq-questionnaire.sessionId", json.id);

          localStorage.removeItem("ciq_can_show_language_prompt");

          const patientPortalUrl = window.location.origin;
          const queryParams = {
            locale,
            redirectUrl: `${patientPortalUrl}/complete`,
          };

          openQuestionnaire({
            token: json.survey_token,
            queryParams,
            context: "remote",
            newTab: false,
            skipLandingPage: false,
          });
        }
      }
    } catch (error) {
      setError(ErrorType.GENERAL);
    }

    // TODO: Can we even get to this point?
  }, [locale]);

  const proceedAfterEhrCheck = useCallback(() => {
    const ehrConnected = sessionStorage.getItem("ehr_connected");

    // Always show EHR connect page to let user confirm/change their connection
    // This gives them the option to:
    // - Continue with existing connection
    // - Refresh their data
    // - Switch to a different provider
    // - Skip and enter manually
    if (!ehrConnected) {
      history.push("/ehr-connect");
      return;
    }

    startSurvey();
  }, [startSurvey, history]);

  const handleStart = useCallback(async () => {
    setSubmitting(true);

    // Wait for EHR connection status to load before deciding
    if (isLoadingEhr) {
      setPendingStart(true);
      return;
    }

    proceedAfterEhrCheck();
  }, [isLoadingEhr, proceedAfterEhrCheck]);

  if (error === ErrorType.AUTH) {
    window.location.href = "/login/passwordless";
  }

  return (
    <>
      {error === ErrorType.GENERAL && <div>Error loading survey</div>}
      <StartAssessment onStartClick={handleStart} isSubmitting={isSubmitting} />
    </>
  );
}
